import { get } from "lodash";
import { normalizeEuroAmount } from "../../functional/normalize";

const transformToTwoDigitDecimal = (value: string | number) => {
  if (
    typeof value === "number" ||
    (typeof value === "string" && Number(value) !== undefined && !isNaN(value as any))
  ) {
    const numberValue = Number(value);
    const fixed = numberValue.toFixed(2);
    return fixed.replace(".", ",");
  }
  return value;
};

const mapStorefrontToModel = (data: any) => {
  const model: any = {};

  /**
   * The model contains the full datastorage item with exception of:
   * - customerData is mapped to other properties
   * - custom fields are mapped individually
   */
  model.transaction = data.transaction;
  model.financialProduct = data.financialProduct;
  model.vehicleData = data.vehicleData;
  model.dealerData = data.dealerData;
  model.salesChannel = data.salesChannel;
  // generated idNowUrl to start the ident
  if (data.custom) {
    model.idNowUrl = data.custom.idNowUrl;
    model.custom = data.custom;
    model.identificationStarted = data.custom.identificationStarted
  }

  mapCustomerData(model, data);

  return model;
};

/**
 * Legacy mapping of customer data object
 * @param {*} model
 * @param {*} data
 */
function mapCustomerData(model: any, data: any) {
  model.partnerType = get(data, "customerData.partnerType");
  model.salutation = get(data, "customerData.salutation");
  model.title = get(data, "customerData.title");
  model.firstName = get(data, "customerData.firstName");
  model.lastName = get(data, "customerData.lastName");
  model.birthDate = get(data, "customerData.dateOfBirth"); // birthDate<>dateOfBirth
  model.placeOfBirth = get(data, "customerData.placeOfBirth");
  model.maidenName = get(data, "customerData.maidenName") || "";
  model.maritalStatus = get(data, "customerData.maritalStatus");
  model.nationality = get(data, "customerData.nationality");
  model.email = get(data, "customerData.contactData.email"); // level difference
  model.mobilePhoneCountryCode =
    get(data, "customerData.contactData.mobilePhoneCountryCode") || "+49"; // level difference
  model.mobilePhoneNumber = get(
    data,
    "customerData.contactData.mobilePhoneNumber" // level difference
  );
  model.alimentChildrenCount = get(
    data,
    "customerData.numberOfDependentChildren"
  )?.toString(); // alimentChildrenCount<>numberOfDependentChildren
  const propertySelfUse =  get(data, "customerData.financialDisclosure.propertySelfUse");
  model.selfUsedProperty = propertySelfUse ? "yes" : propertySelfUse === false ? "no" : "";

  const livingStatus = get(data, "customerData.isLivingTogether");
  if (livingStatus === true) {
    model.livingStatus = "yes";
  } else if (livingStatus === false) {
    model.livingStatus = "no";
  }

  model.street = get(data, "customerData.addresses[0].street");
  model.housenumber = get(data, "customerData.addresses[0].houseNumber");
  model.postcode = get(data, "customerData.addresses[0].zipCode");
  model.city = get(data, "customerData.addresses[0].city");
  model.country = get(data, "customerData.addresses[0].countryCode");
  model.countryCode = get(data, "customerData.addresses[0].countryCode");
  model.prevstreet = get(data, "customerData.addresses[1].street");
  model.prevhousenumber = get(data, "customerData.addresses[1].houseNumber");
  model.prevpostcode = get(data, "customerData.addresses[1].zipCode");
  model.prevcity = get(data, "customerData.addresses[1].city");
  model.prevcountry = get(data, "customerData.addresses[1].countryCode");

  model.residentSince = get(
    data,
    "customerData.addresses[0].residentSinceDate"
  );
  model.employedSince = get(
    data,
    "customerData.employment.employmentStartDate"
  );
  model.employmentLimit =
    get(data, "customerData.employment.employmentEndDate") || "";
  model.professionGroup = get(data, "customerData.employment.occupation");
  model.employerName = get(data, "customerData.employment.employer.name");
  model.employerLocation = get(data, "customerData.employment.employer.city");
  model.industryOfOccupation = get(data, "customerData.employment.industryId");

  const permanentlyEmployed = get(data, "customerData.employment.permanentlyEmployed");
  model.employment = permanentlyEmployed ? "unlimited" : permanentlyEmployed === false ? "limited" : "";

  model.iban = get(data, "customerData.paymentData.iban");
  model.bic = get(data, "customerData.paymentData.bic");
  model.bankName = get(data, "customerData.paymentData.bankName");

  model.additionalNetIncome = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyIncome.additionalIncomeAmount"
      ) || ""
    ).toString()
  );
  model.netIncome = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyIncome.netIncomeAmount"
      ) || ""
    ).toString()
  );
  model.livingConditions = get(
    data,
    "customerData.financialDisclosure.housingSituation"
  );
  model.monthlyRateLease = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyExpenses.rentAmount"
      ) || ""
    ).toString()
  );
  model.insuranceAmount = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyExpenses.insuranceAmount"
      ) || ""
    ).toString()
  );
  model.monthlyRateProperty = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyExpenses.mortgageAmount"
      ) || ""
    ).toString()
  );
  model.monthlyExpenses = normalizeEuroAmount(
    (
      get(data, "customerData.financialDisclosure.monthlyExpenses.total") || ""
    ).toString()
  );

  // new fields for full app form in heycar
  model.profession = get(data, "customerData.employment.profession");
  model.isLivingInDeStartDate = get(
    data,
    "customerData.addresses[0].isLivingInDeStartDate"
  );
  model.residencePermitStatus = get(
    data,
    "customerData.addresses[0].residencePermitStatus"
  );
  model.workPermitStatus = get(
    data,
    "customerData.employment.workPermitStatus"
  );
  model.residencePermitEndDate =
    get(data, "customerData.addresses[0].residencePermitEndDate") || "";
  model.workPermitEndDate =
    get(data, "customerData.employment.workPermitEndDate") || "";

  const creditsAndLoans = get(
    data,
    "customerData.financialDisclosure.monthlyExpenses.credits"
  );
  if (creditsAndLoans) {
    model.creditsAndLoans = "yes";
  } else if (creditsAndLoans === false) {
    model.creditsAndLoans = "no";
  }
  model.monthlyCreditRate = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyExpenses.monthlyCreditRate"
      ) || ""
    ).toString()
  );
  model.extraCost = normalizeEuroAmount(
    (
      get(
        data,
        "customerData.financialDisclosure.monthlyExpenses.ancillaryCostAmount"
      ) || ""
    ).toString()
  );

  // Can be undefined/yes/no
  const isMajorityShareholder = data.customerData?.financialDisclosure?.isMajorityShareholder;
  model.isMajorityShareholder = isMajorityShareholder === undefined ? "" : (isMajorityShareholder ? "yes" : "no");
  model.majorityShareholderCompanyName = isMajorityShareholder ? data.customerData?.financialDisclosure?.majorityShareholderCompany?.name : "";
  model.majorityShareholderCompanyCity = isMajorityShareholder ? data.customerData?.financialDisclosure?.majorityShareholderCompany?.city : "";

  // Can be undefined/yes/no
  const isLiablePartner = data.customerData?.financialDisclosure?.isLiablePartner;
  model.isLiablePartner = isLiablePartner === undefined ? "" : (isLiablePartner ? "yes" : "no");
  model.liablePartnerCompanyName = isLiablePartner ? data.customerData?.financialDisclosure?.liablePartnerCompany?.name : "";
  model.liablePartnerCompanyCity = isLiablePartner ? data.customerData?.financialDisclosure?.liablePartnerCompany?.city : "";
}

function getEmptyPrefillObject() {
  return {
    /**
     * Values mapped to data storage item properties (in PrefillMapping.jsx)
     */
    transaction: undefined,
    financialProduct: undefined,
    vehicleData: undefined,
    dealerData: undefined,
    salesChannel: undefined,
    custom: undefined,
    idNowUrl: "",
    /**
     * Form values mapped to properties in customerData on various levels.
     */
    firstName: "",
    salutation: "",
    title: "",
    lastName: "",
    maidenName: "",
    birthDate: "",
    placeOfBirth: "",
    nationality: "",
    maritalStatus: "",
    livingStatus: "",
    fiscalNumber: "",
    street: "",
    housenumber: "",
    postcode: "",
    city: "",
    country: "",
    prevstreet: "",
    prevhousenumber: "",
    prevpostcode: "",
    prevcity: "",
    prevcountry: "",
    residentSince: "",
    email: "",
    mobilePhoneCountryCode: "+49",
    mobilePhoneNumber: "",
    professionGroup: "",
    profession: "",
    employment: "",
    business: "",
    employerName: "",
    selfUsedProperty: "",
    employerLocation: "",
    employedSince: "",
    employmentLimit: "",
    netIncome: "",
    additionalNetIncome: "",
    livingConditions: "",
    monthlyRateLease: "",
    monthlyRateProperty: "",
    extraCost: "",
    alimentChildrenCount: "",
    activeCreditContracts: "",
    iban: "",
    documents: [],
    documentsArray: undefined,
    credits: [],
    isLivingInDeStartDate: "",
    residencePermitStatus: "",
    workPermitStatus: "",
    residencePermitEndDate: "",
    workPermitEndDate: "",
    creditsAndLoans: "",
    monthlyCreditRate: "",
    isMajorityShareholder: "",
    majorityShareholderCompanyName: "",
    majorityShareholderCompanyCity: "",
    isLiablePartner: "",
    liablePartnerCompanyName: "",
    liablePartnerCompanyCity: "",
  };
}

export { mapStorefrontToModel, transformToTwoDigitDecimal, getEmptyPrefillObject };
